import {
  COLORS,
  GenericIconName,
  Icon,
  TextInput,
  useShowToast,
} from '@aboutyou/ui-components';
import React, {FC, useState} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {ChangePasswordButton} from './Components/ChangePasswordButton';
import {PasswordStrengthWatch} from './Components/PasswordStrengthWatch';
import {
  Container,
  DefaultForm,
  FormTitle,
  FormWrapper,
  GrayedText,
  InputRow,
  SubmitButtonContainer,
} from './Components/StyledComponents';
import {useUpdateCustomerData} from './hooks/useUpdateCustomerData';

interface ChangePasswordData {
  password: string; // new password
  currentPassword: string;
  confirmPassword: string;
}

const PERMISSION_DENIED = 3;

const defaultValues = {
  password: '',
  currentPassword: '',
  confirmPassword: '',
};

interface LoginFlowTranslations {
  common: {
    navigationTitle: string;
    or: string;
    error: string;
    fieldLabels: {
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      passwordToRegister: string;
      passwordLabels: {
        showPassword: string;
        hidePassword: string;
      };
      gender: string;
      genderSalutations: {
        female: string;
        male: string;
        divers: string;
      };
      newsletterLabel: string;
      newsletterCheckboxText: string;
    };
    fieldErrors: {
      email: {
        empty: string;
        invalid: string;
      };
      firstName: {
        empty: string;
        invalid: string;
      };
      lastName: {
        empty: string;
        invalid: string;
      };
      password: {
        empty: string;
        invalid: string;
        matchesOld: string;
      };
      confirmPassword: {
        invalid: string;
      };
    };
  };
  passwordReset: {
    heading: string;
    copy: string;
    savePassword: string;
    confirmNewPassword: string;
    newPassword: string;
    currentPassword: string;
    resetSuccessful: string;
    error: string;
  };
  toast: {
    changesSaved: string;
  };
  formErrors: {
    phone: {invalid: string};
    email: {exists: string};
    incorrectPassword: string;
    general: string;
  };
  changePasswordHeading: string;
}

export const ChangePassword: FC<{
  translations: LoginFlowTranslations;
}> = ({
  translations: {
    common,
    passwordReset,
    toast,
    formErrors,
    changePasswordHeading,
  },
}) => {
  const {
    handleSubmit,
    formState,
    getValues,
    register,
    control,
    reset: resetForm,
  } = useForm<{
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    criteriaMode: 'firstError',
  });
  const {errors} = formState;

  const showToast = useShowToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateUserPersonalData = useUpdateCustomerData();
  const currentPassword = useWatch({name: 'currentPassword', control});
  const password = useWatch({name: 'password', control});
  const confirmPassword = useWatch({name: 'confirmPassword', control});

  const inputProps = {
    type: 'password',
    passwordLabels: {
      showPassword: common.fieldLabels.passwordLabels.showPassword,
      hidePassword: common.fieldLabels.passwordLabels.hidePassword,
    },
  };

  const onSuccess = () => {
    showToast({
      content: <span>{toast.changesSaved}</span>,
      type: 'SUCCESS',
    });
    resetForm({
      confirmPassword: '',
      currentPassword: '',
      password: '',
    });
  };

  const onError = (error: {code: number}) => {
    if (error?.code === PERMISSION_DENIED) {
      showToast({
        content: formErrors.incorrectPassword,
        type: 'ERROR',
      });
    } else {
      showToast({
        content: formErrors.general,
        type: 'ERROR',
      });
    }
  };

  const onFinally = () => {
    setIsSubmitting(false);
  };

  const onSubmit = (data: ChangePasswordData) => {
    setIsSubmitting(true);
    updateUserPersonalData(
      {
        password: {
          newPassword: data.password,
          currentPassword: data.currentPassword,
        },
      },
      {
        onSuccess,
        onError,
        onFinally,
      },
    );
  };

  return (
    <Container>
      <FormWrapper>
        <DefaultForm
          data-testid="passwordForm"
          method="POST"
          onSubmit={handleSubmit(onSubmit)}
        >
          <InputRow $align $extraMargin>
            <Icon asset={GenericIconName.Lock} size="S" />
            <FormTitle data-testid="formTitle">
              {changePasswordHeading}
            </FormTitle>
          </InputRow>
          <InputRow $extraMargin>
            <TextInput
              {...inputProps}
              activeStyle={{
                backgroundColor: COLORS.White,
                borderColor: COLORS.Black,
              }}
              dataTestId="currentPassword"
              value={currentPassword}
              label={passwordReset.currentPassword}
              errorMessage={errors.currentPassword?.message}
              hideErrorIcon
              {...register('currentPassword', {
                required: common.fieldErrors.password.empty,
                minLength: {
                  value: 6,
                  message: common.fieldErrors.password.invalid,
                },
              })}
            />
          </InputRow>

          <InputRow>
            <TextInput
              {...inputProps}
              activeStyle={{
                backgroundColor: COLORS.White,
                borderColor: COLORS.Black,
              }}
              dataTestId="newPassword"
              value={password}
              label={passwordReset.newPassword}
              errorMessage={errors.password?.message}
              hideErrorIcon
              {...register('password', {
                required: common.fieldErrors.password.empty,
                minLength: {
                  value: 6,
                  message: common.fieldErrors.password.invalid,
                },
                validate: val =>
                  !val ||
                  !getValues('currentPassword') ||
                  val !== getValues('currentPassword') ||
                  common.fieldErrors.password.matchesOld,
              })}
            />
          </InputRow>

          <PasswordStrengthWatch control={control} />

          {/* Only render the password hint once the user enters a new password */}
          {password?.length > 0 && (
            <InputRow $extraMargin>
              <GrayedText data-testid="passwordHint" align="left">
                {passwordReset.copy}
                &#x200E;
              </GrayedText>
            </InputRow>
          )}

          <InputRow>
            <TextInput
              {...inputProps}
              activeStyle={{
                backgroundColor: COLORS.White,
                borderColor: COLORS.Black,
              }}
              dataTestId="confirmPassword"
              value={confirmPassword}
              errorMessage={errors.confirmPassword?.message}
              label={passwordReset.confirmNewPassword}
              hideErrorIcon
              {...register('confirmPassword', {
                required: common.fieldErrors.password.empty,
                minLength: {
                  value: 6,
                  message: common.fieldErrors.password.invalid,
                },
                validate: val =>
                  val === getValues('password') ||
                  common.fieldErrors.confirmPassword.invalid,
              })}
            />
          </InputRow>

          <SubmitButtonContainer>
            <ChangePasswordButton
              isSubmitting={isSubmitting}
              control={control}
              disabled={password?.length < 6}
              buttonLabel={passwordReset.savePassword}
            />
          </SubmitButtonContainer>
        </DefaultForm>
      </FormWrapper>
    </Container>
  );
};
